import { useLoaderData, useSearchParams } from 'react-router';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { RoccoLink } from '@rocco/components/link';
import { Spinner } from '@rocco/components/spinner';
import { cn } from '@rocco/utils/cn';
import { getLogoBadgeBorderColor } from '@rocco/ui/tile-professional/components/logo-badge-border';
import type { loader as networkLoader } from '@modules/professional/page/loaders/network-page-loader.server';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import AppliedFilters from '~/modules/search/components/search-filter-section/AppliedFilters';
import { PROFESSIONAL_TAB_PROFESSIONAL_FILTERS_CONFIG } from '@modules/professional/config/professional-tab-filter-configs';
import { ProfessionalTabFilterSidebar } from './ProfessionalTabFilterSidebar';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import {
    IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
    IN_PAGE_SEARCH_PARAM_KEY,
} from '~/modules/professional/config/constants';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useTabLoadMore } from '@modules/professional/hook/use-tab-load-more';
import { useProfessionalHybridLoad } from '@modules/professional/hook/use-professional-hybrid-load';
import { DetailTileMedia, detailCta } from '~/ui-rocco/pages/detail-shared';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { ProfessionalHeadingCtas } from './ProfessionalHeadingCtas';

const NETWORK_DESCRIPTION =
    "We've curated New Zealand's leading professionals so you can find the best fit for your project. Browse their work, discover their services and enquire directly from ArchiPro.";

/**
 * Network tab (/partners) body — an "(All Network)" divider, the collapsible
 * left filters reused from the legacy tab and a 4-column grid of partner-
 * professional tiles (portrait image + corner logo badge + name + category),
 * matching the Figma. Driven by the network ES loader (tiles + pagination +
 * filters), with infinite scroll appending further pages. Self-hides when empty
 * and unfiltered; the name / stats / CTA sit in the persistent profile header.
 */
export const ProfessionalNetworkGridSection = () => {
    const {
        networkProfessionals,
        networkHasMore,
        pagination,
        filterStates,
        filterStatesRocco,
        aggregateScopeFilters,
    } = useLoaderData<typeof networkLoader>();
    const { URLSegment } = useProfessionalInitialRouteData();
    const { desktop } = useAppDisplayModeContext();

    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();
    const [searchParams] = useSearchParams();

    const {
        items: professionals,
        hasMore,
        isLoading,
        loadMore,
    } = useTabLoadMore({
        initialItems: networkProfessionals,
        initialHasMore: networkHasMore,
        initialPage: pagination.page,
        urlSegment: URLSegment,
        searchType: 'professional',
        getKey: (pro) => pro.link,
    });

    const { showViewMore, onViewMore, infiniteEnabled, sentinelRef } =
        useProfessionalHybridLoad({
            hasMore,
            isLoading,
            loadMore,
            resetKey: searchParams.toString(),
        });

    const category = useMatchesHandleData<SearchIndex>(
        'category',
        'professional',
    );
    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys: IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
    });

    const hasAppliedFilters =
        !!searchFilters.appliedFilters &&
        searchFilters.appliedFilters.length > 0;

    // A search term lives in the `q` param, not in appliedFilters — so a
    // 0-result search must also keep the section mounted, otherwise the
    // search bar disappears and there's no way to clear it.
    const hasActiveSearch = !!searchParams.get(IN_PAGE_SEARCH_PARAM_KEY);

    if (professionals.length === 0 && !hasAppliedFilters && !hasActiveSearch)
        return null;

    return (
        <section className="w-full overflow-x-clip bg-white text-[#1f1f21]">
            {/* Desktop keeps the editorial title + intro + CTA cluster; on mobile
                the persistent social-profile header carries the identity/CTAs and
                the tab goes straight to results. */}
            {desktop && (
                <>
                    <div className="mx-auto w-full max-w-[120rem] md:px-[3.132rem] md:pt-[3rem]">
                        <div className="flex md:items-start md:justify-between">
                            <h2 className="m-0 font-sans font-normal text-[#141414] md:text-[12.5rem] md:leading-[0.92] md:tracking-[-0.12rem]">
                                Network
                            </h2>
                            <ProfessionalHeadingCtas />
                        </div>
                        <p className="m-0 font-sans leading-[1.25] font-normal text-[#1f1f21] md:mt-[5.9375rem] md:max-w-[60.375rem] md:text-[2.0625rem]">
                            {NETWORK_DESCRIPTION}
                        </p>
                    </div>
                    <div className="mx-auto w-full max-w-[120rem] md:px-[3.132rem] md:pt-[10rem]">
                        <div className="h-px w-full bg-black/20" />
                        <SectionLabel className="mt-[1.062rem] block text-[#181818] md:text-[2rem]">
                            (All Network)
                        </SectionLabel>
                    </div>
                </>
            )}
            {/* Filters + grid, straight to results (the tab pill names the page,
                the persistent header carries the identity). Default light theme. */}
            <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-5 max-md:pb-14 md:px-[3.132rem] md:pt-[2.4rem] md:pb-[7.5rem]">
                <CollapsiblePageLayout
                    paddingClassName="px-0"
                    sidePanel={
                        desktop &&
                        filterStatesRocco && (
                            <div className="h-full md:pt-[1.8rem]">
                                <ProfessionalTabFilterSidebar
                                    searchIndex="professional"
                                    config={
                                        PROFESSIONAL_TAB_PROFESSIONAL_FILTERS_CONFIG
                                    }
                                    initialFilterStates={filterStatesRocco}
                                    aggregateScopeFilters={
                                        aggregateScopeFilters
                                    }
                                />
                            </div>
                        )
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <SearchFilter
                        searchFilters={searchFilters}
                        filterDrawerOpen={filterDrawerOpen}
                        toggleFilterDrawer={toggleFilterDrawer}
                        searchBarVariant="revamp"
                        searchPlaceholder="Search network"
                    />
                    {/* The applied-filter bar is desktop-only; on mobile the
                        Refine button's badge + sheet already surface these. */}
                    {desktop &&
                        searchFilters.appliedFilters &&
                        searchFilters.appliedFilters.length > 0 && (
                            <AppliedFilters
                                filterItems={searchFilters.appliedFilters}
                                resetFilters={() =>
                                    searchFilters.resetFilters(
                                        resetFilterStates(
                                            searchFilters.filterStates,
                                            [
                                                ...IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
                                                'category',
                                            ],
                                        ),
                                    )
                                }
                            />
                        )}

                    {professionals.length === 0 ? (
                        <NoSearchResults resultsType={'professionals'} />
                    ) : (
                        <>
                            <div
                                className={cn(
                                    'grid grid-cols-2 gap-x-2 gap-y-8 pt-[2.4rem] md:gap-x-[1.5rem] md:gap-y-[6.25rem]',
                                    filterDrawerOpen
                                        ? 'md:grid-cols-3'
                                        : 'md:grid-cols-4',
                                )}
                            >
                                {professionals.map((pro, index) => (
                                    <RoccoLink
                                        key={`${pro.link}-${index}`}
                                        to={pro.link}
                                        className="flex flex-col gap-[0.625rem]"
                                    >
                                        <DetailTileMedia
                                            src={pro.image}
                                            alt={pro.title}
                                            className="bg-neutral-200 aspect-[437/524]"
                                        >
                                            {pro.logo ? (
                                                <div
                                                    className="absolute flex items-center justify-center overflow-hidden rounded-full max-md:top-3 max-md:left-3 max-md:size-12 md:top-[1.875rem] md:left-[1.875rem] md:size-[6.25rem]"
                                                    style={{
                                                        backgroundColor:
                                                            pro.logoBackground ||
                                                            '#ffffff',
                                                        ...(getLogoBadgeBorderColor(
                                                            pro.logoBackground,
                                                        )
                                                            ? {
                                                                  border: `1px solid ${getLogoBadgeBorderColor(pro.logoBackground)}`,
                                                              }
                                                            : {}),
                                                    }}
                                                >
                                                    <img
                                                        src={pro.logo}
                                                        alt=""
                                                        loading="lazy"
                                                        className="size-full object-contain p-[0.5rem]"
                                                    />
                                                </div>
                                            ) : null}
                                        </DetailTileMedia>
                                        <div className="flex flex-col leading-[1.3]">
                                            <p className="m-0 truncate font-sans font-medium text-[#181818] max-md:text-base md:text-[1.375rem]">
                                                {pro.title}
                                            </p>
                                            <p className="m-0 truncate font-sans font-normal text-[#181818]/40 max-md:text-base md:text-[1.375rem]">
                                                {pro.category}
                                            </p>
                                        </div>
                                    </RoccoLink>
                                ))}
                            </div>
                            {infiniteEnabled && hasMore && (
                                <div
                                    ref={sentinelRef}
                                    aria-hidden="true"
                                    style={{ height: 1 }}
                                />
                            )}
                            {showViewMore && (
                                <div className="flex justify-center pt-[3rem]">
                                    <button
                                        type="button"
                                        onClick={onViewMore}
                                        className={detailCta(
                                            'outlined-light',
                                            'max-md:h-[3.125rem] max-md:text-sm',
                                        )}
                                    >
                                        View more
                                    </button>
                                </div>
                            )}
                            {isLoading && (
                                <div className="flex justify-center pt-[3rem]">
                                    <Spinner className="size-8 text-[#181818]" />
                                </div>
                            )}
                        </>
                    )}
                </CollapsiblePageLayout>
            </div>
        </section>
    );
};
